import { Address, ChainKey, WalletKey, WalletOption } from "@silo/types";
import { useState } from "react";
import { match } from "ts-pattern";
import { AccountDetails } from "./account-details";
import { SelectWallet } from "./select-wallet";
import { AccountDetailsTransaction, ConnectWallet } from "./types";
import { Failed } from "./failed";

interface ConnectedFlowProps {
  isImpersonating: boolean;
  account: Address;
  ensName: string | undefined;
  disconnect: () => void;
  activeWalletKey: WalletKey;
  chainKey: ChainKey;
  recentTransactions: AccountDetailsTransaction[];
  clearTransactions: () => void;
  walletOptions: WalletOption[];
  connectWallet: ConnectWallet;
  onConnect?: () => void;
}

type ConnectedState =
  | { _tag: "viewing" }
  | { _tag: "selecting" }
  | { _tag: "failed"; errorMessage?: string; walletKey: WalletKey };

export function ConnectedFlow({
  isImpersonating,
  account,
  ensName,
  disconnect,
  activeWalletKey,
  chainKey,
  recentTransactions,
  clearTransactions,
  walletOptions,
  connectWallet,
  onConnect,
}: ConnectedFlowProps): JSX.Element {
  const [state, setState] = useState<ConnectedState>({ _tag: "viewing" });
  const [isPending, setIsPending] = useState(false);

  function select(): void {
    setState({ _tag: "selecting" });
  }

  function selectWallet(walletKey: WalletKey): void {
    if (walletKey === activeWalletKey) {
      setState({ _tag: "viewing" });
      return;
    }

    async function fn() {
      try {
        setIsPending(true);

        await connectWallet(walletKey, activeWalletKey ?? undefined);

        onConnect?.();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : undefined;

        setState({ _tag: "failed", errorMessage, walletKey });
      } finally {
        setIsPending(false);
      }
    }

    fn();
  }

  return match(state)
    .with({ _tag: "viewing" }, () => (
      <AccountDetails
        isImpersonating={isImpersonating}
        account={account}
        ensName={ensName}
        disconnect={disconnect}
        change={select}
        activeWalletKey={activeWalletKey}
        chainKey={chainKey}
        recentTransactions={recentTransactions}
        clearTransactions={clearTransactions}
      />
    ))
    .with({ _tag: "selecting" }, () => (
      <SelectWallet
        walletOptions={walletOptions}
        isPending={isPending}
        activeWalletKey={activeWalletKey}
        selectWallet={selectWallet}
      />
    ))
    .with({ _tag: "failed" }, ({ errorMessage, walletKey }) => (
      <Failed
        errorMessage={errorMessage}
        walletKey={walletKey}
        selectWallet={selectWallet}
        isPending={isPending}
        goBack={select}
      />
    ))
    .exhaustive();
}
